import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Scale, Percent } from 'lucide-react';
import { CostLine, FinanceApp, FinanceReport, financeApi } from '../../api/finance';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';
import {
  SERIES,
  compact,
  count,
  isoDay,
  longDate,
  monthLabel,
  muted,
  naira,
  sectionNote,
  sectionTitle,
  td,
  tdRight,
  th,
  thRight,
} from './shared';

type Preset = 'this_month' | 'last_month' | '3_months' | '6_months' | '12_months' | 'this_year' | 'custom';

const PRESETS: { key: Exclude<Preset, 'custom'>; label: string }[] = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: '3_months', label: '3 months' },
  { key: '6_months', label: '6 months' },
  { key: '12_months', label: '12 months' },
  { key: 'this_year', label: 'This year' },
];

/** Whole months, so the chart's first and last bars are not half-months. */
export function rangeFor(preset: Exclude<Preset, 'custom'>, now = new Date()): { from: string; to: string } {
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = isoDay(now);
  switch (preset) {
    case 'this_month':
      return { from: isoDay(new Date(y, m, 1)), to: today };
    case 'last_month':
      return { from: isoDay(new Date(y, m - 1, 1)), to: isoDay(new Date(y, m, 0)) };
    case '3_months':
      return { from: isoDay(new Date(y, m - 2, 1)), to: today };
    case '6_months':
      return { from: isoDay(new Date(y, m - 5, 1)), to: today };
    case '12_months':
      return { from: isoDay(new Date(y, m - 11, 1)), to: today };
    case 'this_year':
      return { from: isoDay(new Date(y, 0, 1)), to: today };
  }
}

const APP_ORDER: FinanceApp[] = ['consumer', 'business', 'commerce'];

/**
 * Money in against money out.
 *
 * Revenue is only what PayIQ keeps; costs are what it pays providers, what
 * it pays out to people, and its fixed costs. The figures come straight from
 * the server, which says how each is worked out in each line's note.
 */
export const ReportPanel: React.FC<{ onSetPrices: () => void }> = ({ onSetPrices }) => {
  const [preset, setPreset] = useState<Preset>('6_months');
  const [range, setRange] = useState(() => rangeFor('6_months'));
  const [draft, setDraft] = useState(range);
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await financeApi.report(from, to));
    } catch {
      setError('Could not load the report. Try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(range.from, range.to);
  }, [load, range]);

  const choose = (p: Exclude<Preset, 'custom'>) => {
    const next = rangeFor(p);
    setPreset(p);
    setRange(next);
    setDraft(next);
  };

  const draftValid = draft.from && draft.to && draft.from <= draft.to;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
        <div
          role="group"
          aria-label="Period"
          style={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            gap: 2,
            padding: 4,
            background: 'var(--neutral-100)',
            border: '1px solid var(--neutral-300)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {PRESETS.map((p) => {
            const active = preset === p.key;
            return (
              <button
                key={p.key}
                type="button"
                aria-pressed={active}
                onClick={() => choose(p.key)}
                style={{
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  fontFamily: 'var(--font-family)',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  cursor: 'pointer',
                  background: active ? 'var(--blue-600)' : 'transparent',
                  color: active ? 'var(--neutral-100)' : 'var(--neutral-700)',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!draftValid) return;
            setPreset('custom');
            setRange({ ...draft });
          }}
          style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}
        >
          <Input
            label="From"
            type="date"
            value={draft.from}
            max={draft.to}
            onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
          />
          <Input
            label="To"
            type="date"
            value={draft.to}
            min={draft.from}
            onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
          />
          <Button type="submit" variant="secondary" disabled={!draftValid}>
            Show
          </Button>
        </form>
      </div>

      {loading && !report && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      )}
      {error && (
        <div role="alert" style={{ color: 'var(--danger-900)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {report && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24, opacity: loading ? 0.6 : 1 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--neutral-600)' }}>
            {longDate(report.from)} to {longDate(report.to)}
          </p>

          <Notices report={report} onSetPrices={onSetPrices} />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16,
            }}
          >
            <StatCard label="Money in" value={naira(report.totals.revenue)} icon={ArrowDownLeft} accent="blue" />
            <StatCard label="Money out" value={naira(report.totals.costs)} icon={ArrowUpRight} accent="neutral" />
            <StatCard
              label={report.totals.net < 0 ? 'Loss' : 'Profit'}
              value={naira(report.totals.net)}
              icon={Scale}
              accent={report.totals.net < 0 ? 'neutral' : 'green'}
            />
            <StatCard
              label="Margin"
              value={
                report.totals.revenue > 0
                  ? `${((report.totals.net / report.totals.revenue) * 100).toFixed(1)}%`
                  : 'No revenue'
              }
              icon={Percent}
              accent="purple"
            />
          </div>

          <MonthChart report={report} />

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
              gap: 24,
              alignItems: 'start',
            }}
          >
            <MoneyIn report={report} />
            <MoneyOutSummary report={report} />
          </div>

          <ProviderFees report={report} onSetPrices={onSetPrices} />
          <Payouts lines={report.costs.lines.filter((l) => l.kind === 'payouts')} />
          <Fixed report={report} />
        </div>
      )}
    </div>
  );
};

const Notices: React.FC<{ report: FinanceReport; onSetPrices: () => void }> = ({ report, onSetPrices }) => {
  const unpricedUses = report.unpriced.reduce((s, u) => s + u.quantity, 0);
  if (!unpricedUses && report.fxConfigured && !report.warnings.length) return null;
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        gap: 12,
        padding: 16,
        background: 'var(--warning-100)',
        border: '1px solid var(--warning-600)',
        borderRadius: 'var(--radius-md)',
        fontSize: 13,
        color: 'var(--warning-900)',
      }}
    >
      <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {unpricedUses > 0 && (
          <div>
            <strong>Costs read low.</strong> {count(unpricedUses)} uses of{' '}
            {report.unpriced.length === 1 ? 'one product' : `${report.unpriced.length} products`} have no price yet (
            {report.unpriced.map((u) => `${u.providerLabel} ${u.label.toLowerCase()}`).join(', ')}), so they count as
            nothing.{' '}
            <button
              type="button"
              onClick={onSetPrices}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--blue-700)',
                textDecoration: 'underline',
                cursor: 'pointer',
                font: 'inherit',
              }}
            >
              Set prices
            </button>
          </div>
        )}
        {!report.fxConfigured && (
          <div>
            <strong>No dollar rate is set,</strong> so costs in dollars (AI models, and any price in USD) read as ₦0.
            Set the rate in the card FX settings.
          </div>
        )}
        {report.warnings.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>
    </div>
  );
};

interface ChartTooltipProps {
  active?: boolean;
  payload?: { dataKey: string; value: number }[];
  label?: string;
}

const ChartTooltip: React.FC<ChartTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const get = (k: string) => payload.find((p) => p.dataKey === k)?.value ?? 0;
  const rows = [
    { key: 'revenue', label: 'Money in', color: SERIES.revenue },
    { key: 'costs', label: 'Money out', color: SERIES.costs },
    { key: 'net', label: get('net') < 0 ? 'Loss' : 'Profit', color: SERIES.net },
  ];
  return (
    <div
      style={{
        background: 'var(--neutral-100)',
        border: '1px solid var(--neutral-300)',
        borderRadius: 'var(--radius-md)',
        padding: '10px 14px',
        fontSize: 13,
        minWidth: 180,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--neutral-900)' }}>{label}</div>
      {rows.map((r) => (
        <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--neutral-700)' }}>
            <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: r.color }} />
            {r.label}
          </span>
          <span style={{ color: 'var(--neutral-900)', fontVariantNumeric: 'tabular-nums' }}>{naira(get(r.key))}</span>
        </div>
      ))}
    </div>
  );
};

const MonthChart: React.FC<{ report: FinanceReport }> = ({ report }) => {
  const data = useMemo(
    () => report.months.map((m) => ({ ...m, label: monthLabel(m.month) })),
    [report.months],
  );
  const [table, setTable] = useState(false);
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div>
          <h3 style={sectionTitle}>Month by month</h3>
          <p style={sectionNote}>What came in, what went out, and what was left.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setTable((t) => !t)}>
          {table ? 'Show chart' : 'Show table'}
        </Button>
      </div>
      {table ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Month</th>
                <th style={thRight}>Money in</th>
                <th style={thRight}>Money out</th>
                <th style={thRight}>Profit</th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.month}>
                  <td style={td}>{m.label}</td>
                  <td style={tdRight}>{naira(m.revenue)}</td>
                  <td style={tdRight}>{naira(m.costs)}</td>
                  <td style={{ ...tdRight, color: m.net < 0 ? 'var(--danger-700)' : undefined }}>{naira(m.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <ComposedChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={2}>
              <CartesianGrid vertical={false} stroke="var(--neutral-200)" />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={{ stroke: 'var(--neutral-300)' }}
                tick={{ fontSize: 12, fill: 'var(--neutral-600)' }}
              />
              <YAxis
                tickFormatter={compact}
                tickLine={false}
                axisLine={false}
                width={64}
                tick={{ fontSize: 12, fill: 'var(--neutral-600)' }}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--neutral-200)', opacity: 0.5 }} />
              <Legend
                iconType="square"
                iconSize={10}
                wrapperStyle={{ fontSize: 13, color: 'var(--neutral-700)' }}
                formatter={(value: string) => <span style={{ color: 'var(--neutral-700)' }}>{value}</span>}
              />
              <Bar dataKey="revenue" name="Money in" fill={SERIES.revenue} radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Bar dataKey="costs" name="Money out" fill={SERIES.costs} radius={[4, 4, 0, 0]} maxBarSize={28} />
              <Line
                dataKey="net"
                name="Profit"
                // Straight: a curve between months overshoots, drawing a
                // loss or a profit that no month had.
                type="linear"
                stroke={SERIES.net}
                strokeWidth={2}
                dot={{ r: 4, strokeWidth: 2, stroke: 'var(--neutral-100)', fill: SERIES.net }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

const MoneyIn: React.FC<{ report: FinanceReport }> = ({ report }) => (
  <Card>
    <h3 style={sectionTitle}>Money in, by app</h3>
    <p style={sectionNote}>
      Only what PayIQ keeps. Safe Haven's commission, VAT and stamp duty pass through PayIQ and are left out.
    </p>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {APP_ORDER.map((app) => {
          const total = report.revenue.byApp.find((a) => a.app === app);
          const lines = report.revenue.lines.filter((l) => l.app === app);
          return (
            <React.Fragment key={app}>
              <tr>
                <td style={{ ...td, fontWeight: 600, background: 'var(--neutral-200)' }}>{total?.label ?? app}</td>
                <td style={{ ...tdRight, fontWeight: 600, background: 'var(--neutral-200)' }}>
                  {naira(total?.amount ?? 0)}
                </td>
              </tr>
              {lines.map((l) => (
                <tr key={l.key}>
                  <td style={{ ...td, paddingLeft: 24 }}>
                    <div style={{ color: l.amount ? 'var(--neutral-900)' : 'var(--neutral-500)' }}>{l.label}</div>
                    <div style={muted}>
                      {l.quantity ? `${count(l.quantity)} · ` : ''}
                      {l.note}
                    </div>
                  </td>
                  <td style={{ ...tdRight, color: l.amount ? undefined : 'var(--neutral-500)' }}>{naira(l.amount)}</td>
                </tr>
              ))}
            </React.Fragment>
          );
        })}
        <tr>
          <td style={{ ...td, fontWeight: 600 }}>Total</td>
          <td style={{ ...tdRight, fontWeight: 600 }}>{naira(report.totals.revenue)}</td>
        </tr>
      </tbody>
    </table>
  </Card>
);

const MoneyOutSummary: React.FC<{ report: FinanceReport }> = ({ report }) => {
  const providers = report.costs.byProvider.filter((p) => p.kind === 'provider');
  const max = Math.max(1, ...providers.map((p) => p.amount));
  return (
    <Card>
      <h3 style={sectionTitle}>Money out, by provider</h3>
      <p style={sectionNote}>The fees PayIQ pays the companies it runs on, then everything else.</p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {providers.map((p) => (
            <tr key={p.provider}>
              <td style={td}>
                <div>{p.label}</div>
                <div
                  aria-hidden
                  style={{
                    marginTop: 6,
                    height: 6,
                    borderRadius: 3,
                    background: SERIES.costs,
                    width: `${Math.max(p.amount > 0 ? 2 : 0, (p.amount / max) * 100)}%`,
                  }}
                />
              </td>
              <td style={tdRight}>{naira(p.amount)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...td, fontWeight: 600 }}>Provider fees</td>
            <td style={{ ...tdRight, fontWeight: 600 }}>{naira(report.totals.providerFees)}</td>
          </tr>
          <tr>
            <td style={td}>Rewards and commissions</td>
            <td style={tdRight}>{naira(report.totals.payouts)}</td>
          </tr>
          <tr>
            <td style={td}>Fixed costs</td>
            <td style={tdRight}>{naira(report.totals.fixed)}</td>
          </tr>
          <tr>
            <td style={{ ...td, fontWeight: 600 }}>Total</td>
            <td style={{ ...tdRight, fontWeight: 600 }}>{naira(report.totals.costs)}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
};

const CostTable: React.FC<{ lines: CostLine[]; showProvider?: boolean }> = ({ lines, showProvider = true }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
      <thead>
        <tr>
          {showProvider && <th style={th}>Provider</th>}
          <th style={th}>What</th>
          <th style={thRight}>Used</th>
          <th style={thRight}>Cost</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.key}>
            {showProvider && <td style={td}>{l.providerLabel}</td>}
            <td style={td}>
              <div>{l.label}</div>
              {l.note && <div style={muted}>{l.note}</div>}
            </td>
            <td style={tdRight}>
              {count(l.quantity)}
              <div style={muted}>{l.product ? (l.unit ?? 'used') : l.counts}</div>
            </td>
            <td style={tdRight}>
              {naira(l.amount)}
              {l.unpriced > 0 && (
                <div style={{ marginTop: 4 }}>
                  <Badge variant="warning">{count(l.unpriced)} not priced</Badge>
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ProviderFees: React.FC<{ report: FinanceReport; onSetPrices: () => void }> = ({ report, onSetPrices }) => {
  const all = report.costs.lines.filter((l) => l.kind === 'provider');
  // Free transfers get a panel of their own above the table: it is the one
  // cost PayIQ decides on, rather than one it is charged.
  const freeTransfers = all.find((l) => l.key === 'free_transfers');
  const lines = all.filter((l) => l.key !== 'free_transfers');
  return (
    <Card>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div>
          <h3 style={sectionTitle}>Provider fees</h3>
          <p style={sectionNote}>
            Per-use fees are counted from every call and priced from Provider prices. AI models carry their own cost.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onSetPrices}>
          Provider prices
        </Button>
      </div>
      {freeTransfers && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
            padding: 16,
            marginBottom: 16,
            background: 'var(--blue-100)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <div style={{ maxWidth: 560 }}>
            <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>Free transfers</div>
            <div style={{ fontSize: 13, color: 'var(--neutral-700)', marginTop: 4 }}>
              {count(freeTransfers.quantity)} free {freeTransfers.quantity === 1 ? 'transfer' : 'transfers'}.{' '}
              {freeTransfers.note}
            </div>
          </div>
          <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--neutral-900)', whiteSpace: 'nowrap' }}>
            {naira(freeTransfers.amount)}
          </div>
        </div>
      )}
      {lines.length ? (
        <CostTable lines={lines} />
      ) : (
        <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>No provider use in this period.</p>
      )}
    </Card>
  );
};

const Payouts: React.FC<{ lines: CostLine[] }> = ({ lines }) => (
  <Card>
    <h3 style={sectionTitle}>Rewards and commissions</h3>
    <p style={sectionNote}>Money PayIQ chooses to pay people: referral rewards, and aggregators' share of revenue.</p>
    {lines.length ? (
      <CostTable lines={lines} showProvider={false} />
    ) : (
      <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>Nothing paid out in this period.</p>
    )}
  </Card>
);

const CADENCE: Record<string, string> = { monthly: 'Monthly', yearly: 'Yearly', once: 'One-off' };

const Fixed: React.FC<{ report: FinanceReport }> = ({ report }) => (
  <Card>
    <h3 style={sectionTitle}>Fixed costs</h3>
    <p style={sectionNote}>
      Servers, software and the rest, from the Fixed costs tab. A monthly cost counts for the days of it in this period.
    </p>
    {report.costs.fixed.length ? (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>Cost</th>
            <th style={th}>Paid</th>
            <th style={thRight}>In this period</th>
          </tr>
        </thead>
        <tbody>
          {report.costs.fixed.map((c) => (
            <tr key={c.id}>
              <td style={td}>
                <div>{c.name}</div>
                {c.vendor && <div style={muted}>{c.vendor}</div>}
              </td>
              <td style={td}>{CADENCE[c.cadence] ?? c.cadence}</td>
              <td style={tdRight}>{naira(c.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ) : (
      <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
        None yet. Add servers, software and other regular costs in the Fixed costs tab.
      </p>
    )}
  </Card>
);
